import { createReducer, on } from '@ngrx/store';
import * as ChatSocketActions from './websocket.actions';
import {
  WebSocketState,
  initialWebSocketState,
} from './websocket.state';
import { WebSocketConnectionStatus, WebSocketMessage } from './websocket.models';

const SOCKET_STATUS: Record<WebSocketConnectionStatus, WebSocketConnectionStatus> = {
  disconnected: 'disconnected',
  connecting: 'connecting',
  connected: 'connected',
  reconnecting: 'reconnecting',
  error: 'error',
};

function getServerDedupeKey(message: WebSocketMessage): string | null {
  return message._id ? `server:${message._id}` : null;
}

function getClientDedupeKey(message: WebSocketMessage): string | null {
  return message.client_message_id
    ? `client:${message.client_message_id}`
    : null;
}

function ensureChannelState(state: WebSocketState, channelId: string) {
  return (
    state.messagesByChannel[channelId] ?? {
      ids: [],
      entities: {},
    }
  );
}

export const webSocketReducer = createReducer(
  initialWebSocketState,

  on(ChatSocketActions.connectSocket, (state) => ({
    ...state,
    connectionStatus: SOCKET_STATUS.connecting,
    connected: false,
    connecting: true,
    error: null,
    subscribedChannelIds: [],
  })),

  on(ChatSocketActions.connectSocketSuccess, (state) => ({
    ...state,
    connectionStatus: SOCKET_STATUS.connected,
    connected: true,
    connecting: false,
    error: null,
  })),

  on(ChatSocketActions.connectSocketFailure, (state, { error }) => ({
    ...state,
    connectionStatus: SOCKET_STATUS.error,
    connected: false,
    connecting: false,
    error,
  })),

  on(ChatSocketActions.disconnectSocket, (state) => ({
    ...state,
    connectionStatus: SOCKET_STATUS.disconnected,
    connected: false,
    connecting: false,
    subscribedChannelIds: [],
  })),

  on(ChatSocketActions.socketClosed, (state) => ({
    ...state,
    connectionStatus: SOCKET_STATUS.disconnected,
    connected: false,
    connecting: false,
    subscribedChannelIds: [],
  })),

  on(ChatSocketActions.subscribeChannelSuccess, (state, { channelId }) => {
    if (state.subscribedChannelIds.includes(channelId)) {
      return state;
    }

    return {
      ...state,
      subscribedChannelIds: [...state.subscribedChannelIds, channelId],
    };
  }),

  on(ChatSocketActions.unsubscribeChannelSuccess, (state, { channelId }) => ({
    ...state,
    subscribedChannelIds: state.subscribedChannelIds.filter(
      (id) => id !== channelId
    ),
  })),

  on(ChatSocketActions.markPendingMessage, (state, payload) => ({
    ...state,
    pendingMessages: {
      ...state.pendingMessages,
      [payload.clientMessageId]: {
        clientMessageId: payload.clientMessageId,
        channelId: payload.channelId,
        message: payload.message,
        messageType: payload.messageType,
        attributes: payload.attributes,
        createdAt: payload.createdAt,
      },
    },
    messageDedupMap: {
      ...state.messageDedupMap,
      [`client:${payload.clientMessageId}`]: true as const,
    },
  })),

  on(ChatSocketActions.resolvePendingMessage, (state, { clientMessageId }) => {
    const updatedPending = { ...state.pendingMessages };
    delete updatedPending[clientMessageId];

    return {
      ...state,
      pendingMessages: updatedPending,
    };
  }),

  on(ChatSocketActions.receiveChannelMessage, (state, { message }) => {
    const serverKey = getServerDedupeKey(message);
    const clientKey = getClientDedupeKey(message);

    const channelState = ensureChannelState(state, message.channel_id);
    const nextIds = channelState.ids.includes(message._id)
      ? channelState.ids
      : [...channelState.ids, message._id];

    const nextDedupMap: Record<string, true> = { ...state.messageDedupMap };
    if (serverKey) {
      nextDedupMap[serverKey] = true;
    }
    if (clientKey) {
      nextDedupMap[clientKey] = true;
    }

    const nextPending = { ...state.pendingMessages };
    if (message.client_message_id) {
      delete nextPending[message.client_message_id];
    }

    return {
      ...state,
      lastEvent: 'message',
      messageDedupMap: nextDedupMap,
      pendingMessages: nextPending,
      messagesByChannel: {
        ...state.messagesByChannel,
        [message.channel_id]: {
          ids: nextIds,
          entities: {
            ...channelState.entities,
            [message._id]: message,
          },
        },
      },
    };
  }),

  on(ChatSocketActions.receiveSocketError, (state, { error }) => ({
    ...state,
    lastEvent: 'error',
    error,
  })),

  on(ChatSocketActions.clearChannelMessages, (state, { channelId }) => {
    const updatedMessages = { ...state.messagesByChannel };
    delete updatedMessages[channelId];

    return {
      ...state,
      messagesByChannel: updatedMessages,
    };
  }),

  on(ChatSocketActions.resetChatSocketState, () => initialWebSocketState)
);
